/**
 * Filtre de neutralité côté navigateur (CLAUDE.md règle 5, PRD §12, ADR 0011) : même liste et même règle de comparaison
 * que `/api/estimate` (`backend/src/text.ts#findPoliticalName`), appliquées aux mesures dictées reçues par un lien de
 * partage ou renvoyées par l'API. Liste : `content/policy/political-names.json` (copiée dans dist/ par ui/build.mjs).
 * Pur : aucune I/O, aucun nom écrit ici (tout vient de la liste).
 */
import type { Measure } from "../../engine/src/types.js";

export interface PoliticalNames {
  names: string[];
  acronyms: string[];
}

/** Minuscules, sans accents, espaces compactés, ponctuation de bord retirée (identique à backend/src/text.ts). */
export function normalizeText(text: string): string {
  return text
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[’']/g, "'")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^[\s.,;:!?«»"'-]+|[\s.,;:!?«»"'-]+$/g, "");
}

const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** Premier nom de la liste trouvé dans le texte (mots entiers ; noms sans casse ni accents, sigles sensibles à la casse), ou null. */
export function findPoliticalName(text: string, list: PoliticalNames): string | null {
  const n = ` ${normalizeText(text).replace(/[^a-z0-9.' -]/g, " ")} `;
  for (const name of list.names) {
    const k = normalizeText(name);
    if (k && new RegExp(`(^|[^a-z0-9])${escapeRe(k)}([^a-z0-9]|$)`).test(n)) return name;
  }
  for (const a of list.acronyms) {
    if (new RegExp(`(^|[^A-Za-z0-9])${escapeRe(a)}([^A-Za-z0-9]|$)`).test(text)) return a;
  }
  return null;
}

/** Textes affichés d'une mesure : libellé, description, arguments partisans / critiques, libellé de l'hypothèse. */
export function measureTexts(m: Pick<Measure, "label" | "description" | "claims" | "hyp">): string[] {
  return [m.label, m.description ?? "", m.claims?.supporters ?? "", m.claims?.critics ?? "", m.hyp?.label ?? ""].filter((t) => typeof t === "string" && t.length > 0);
}

/**
 * Contrôle de neutralité d'une mesure : `null` si aucun texte ne nomme un parti ou une personnalité, sinon le nom trouvé.
 * Liste absente (`null`) → la mesure est refusée par prudence (`"liste indisponible"`).
 */
export function neutralityHit(m: Pick<Measure, "label" | "description" | "claims" | "hyp">, list: PoliticalNames | null): string | null {
  if (!list) return "liste indisponible";
  for (const t of measureTexts(m)) {
    const hit = findPoliticalName(t, list);
    if (hit) return hit;
  }
  return null;
}

/** Forme minimale attendue du fichier (le `_comment` et la `version` sont ignorés). */
export function parsePoliticalNames(raw: unknown): PoliticalNames | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const strs = (x: unknown) => (Array.isArray(x) ? x.filter((s): s is string => typeof s === "string" && s.length > 0) : null);
  const names = strs(o.names);
  const acronyms = strs(o.acronyms);
  return names && acronyms ? { names, acronyms } : null;
}

/** Avis affiché quand une mesure d'un lien est retirée (jamais le nom trouvé : on ne le réaffiche pas). */
export function neutralityNotice(hit: string): string {
  return hit === "liste indisponible"
    ? "Une proposition reçue par le lien a été retirée : le filtre de neutralité n'a pas pu être chargé."
    : "Une proposition reçue par le lien a été retirée : elle nommait un parti ou une personnalité politique (règle de neutralité du jeu : une mesure est décrite par son contenu, jamais par son auteur).";
}
